import { Accordion, Button, DrawerBody, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, useRestoreFocusSource, useRestoreFocusTarget } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { NavLink } from 'react-router';
import { useStores, useWindowSize } from '../../../common/hooks';
import { RoutePaths } from '../../../route-paths';
import { FlexBox } from '../flex-box';
import AdminMenu from './AdminMenu';
import './Header.scss';
import UserProfileMenu from './UserProfileMenu';
import hamburgerIcon from '/assets/images/hamburger-icon.png';
import microsoftLogo from '/assets/images/microsoft-logo.png';

const HeaderImpl: React.FC = () => {
  const {
    userStore,
    awardStore,
  } = useStores();

  const { width } = useWindowSize();
  const [isOpenAppDrawer, setIsOpenAppDrawer] = useState(false);
  const restoreFocusTargetAttributes = useRestoreFocusTarget();
  const restoreFocusSourceAttributes = useRestoreFocusSource();

  const isMobileBreakpoint = useMemo(() => width < 900, [width]);

  const renderHeaderLink = (route: RoutePaths, text: string, searchParams?: string): React.JSX.Element => (
    <NavLink
      to={`${route}${searchParams ?? ''}`}
      onClick={() => setIsOpenAppDrawer(false)}
      className={({ isActive }) =>
        `${isActive ? 'active' : ''}`
      }
      end
    >
      {text}
    </NavLink>
  );

  const renderHeaderNav = (isRenderInAppDrawer: boolean = false) => (
    <FlexBox
      direction={isRenderInAppDrawer ? 'column' : 'row'}
      as='nav'
      aligns={isRenderInAppDrawer ? 'start' : 'center'}
      className='header-nav'
    >
      {renderHeaderLink(RoutePaths.Home, 'Partner Awards')}
      {userStore.isShowPartnerPage && awardStore.isNominationWindowOpen && renderHeaderLink(RoutePaths.SelectAward, 'Nominate')}
      {userStore.isShowPartnerPage && (
        userStore.userInfo?.isWinner ?
          renderHeaderLink(RoutePaths.AwardResources, 'Winner Resources', '?tab=winner-resource') :
          renderHeaderLink(RoutePaths.AwardResources, 'Award Resources', '?tab=award-resource')
      )}
      {!userStore.isShowPartnerPage && userStore.isStaff && renderHeaderLink(RoutePaths.JudgingResources, 'Judging Resources')}
      {!userStore.isShowPartnerPage && userStore.isAdmin && renderHeaderLink(RoutePaths.WinnersAndFinalists, 'Winners & Finalists')}
      {
        !userStore.isShowPartnerPage
        && (userStore.isAdmin || userStore.isRegionLead)
        && (
          <AdminMenu
            renderInAppDrawer={isRenderInAppDrawer ? {
              onMenuItemClick: () => setIsOpenAppDrawer(false),
            } : undefined}
          />
        )
      }
      <UserProfileMenu
        renderInAppDrawer={isRenderInAppDrawer ? {
          onMenuItemClick: () => setIsOpenAppDrawer(false),
        } : undefined}
      />
    </FlexBox>
  );

  const renderNavInAppDrawer = () => (
    <>
      <OverlayDrawer
        {...restoreFocusSourceAttributes}
        as='aside'
        open={isOpenAppDrawer}
        className='app-drawer'
        onOpenChange={(_, { open }) => setIsOpenAppDrawer(open)}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance='subtle'
                aria-label='Close'
                icon={<Dismiss24Regular />}
                onClick={() => setIsOpenAppDrawer(false)}
              />
            }
          >
            Partner Awards
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody>
          <Accordion
            collapsible
            className='app-drawer__accordion'
          >
            {renderHeaderNav(true)}
          </Accordion>
        </DrawerBody>
      </OverlayDrawer>
    </>
  );

  return (
    <>
      <FlexBox
        as='header'
        aligns='center'
        content='start'
      >
        {
          isMobileBreakpoint && (
            <Button
              {...restoreFocusTargetAttributes}
              aria-label='Open app drawer'
              appearance='subtle'
              icon={<img
                src={hamburgerIcon}
                alt=''
                width={30}
                height={30}
              />}
              onClick={() => setIsOpenAppDrawer(true)}
            />
          )
        }
        <NavLink
          to={RoutePaths.Home}
          className='header-logo'
          end
        >
          <img
            width={115}
            height={30}
            alt='microsoft logo'
            src={microsoftLogo}
          />
        </NavLink>
        {
          !isMobileBreakpoint && (
            <div
              className='header-divider'
            />
          )
        }
        {isMobileBreakpoint ? renderNavInAppDrawer() : renderHeaderNav()}
      </FlexBox>
    </>
  );
};

export const Header = observer(HeaderImpl);
